import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Users, Search, Upload, Download, Shield, Mail, Building,
  Calendar, ChevronRight
} from 'lucide-react';

const roleColors = {
  school_admin: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Admin', gradient: 'from-teal-500 to-emerald-500' },
  hr: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'HR', gradient: 'from-amber-400 to-orange-500' },
  teacher: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Teacher', gradient: 'from-indigo-500 to-purple-500' },
};

export default function EmployeeDirectory() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({});

  const fetchEmployees = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (deptFilter) params.append('department', deptFilter);
      const res = await api.get(`/employees?${params.toString()}`);
      const emps = res.data.data.employees;
      setEmployees(emps);
      const depts = [...new Set(emps.map(e => e.department).filter(Boolean))];
      setDepartments(depts);
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  }, [search, deptFilter]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  useEffect(() => {
    employees.forEach(async (u) => {
      if (u.photo_path && !photoUrls[u.user_id]) {
        try {
          const resp = await api.get(`/files/${u.photo_path}`, { responseType: 'blob' });
          setPhotoUrls(prev => ({ ...prev, [u.user_id]: URL.createObjectURL(resp.data) }));
        } catch {}
      }
    });
  }, [employees]);

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/employees/bulk-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.data;
      toast.success(`Imported ${d.created} employees. ${d.skipped} skipped.`);
      if (d.errors.length) toast.warning(`${d.errors.length} rows had errors.`);
      fetchEmployees();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/employees/bulk-template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'employee_bulk_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  return (
    <div className="space-y-6" data-testid="employee-directory">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md shadow-teal-500/20">
            <Users className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Staff Directory</h1>
            <p className="text-sm text-slate-500">{employees.length} {employees.length === 1 ? 'employee' : 'employees'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <Download className="h-4 w-4 mr-1.5" /> Template
          </Button>
          <label className="cursor-pointer">
            <Button size="sm" className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 rounded-lg pointer-events-none" data-testid="bulk-upload-btn">
              <Upload className="h-4 w-4 mr-1.5" /> {uploading ? 'Uploading...' : 'Bulk Import'}
            </Button>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleBulkUpload} disabled={uploading} />
          </label>
          <Link to="/dashboard/users">
            <Button size="sm" className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg shadow-sm" data-testid="add-staff-btn">
              + Add Staff
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by name, email, ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="search-employees" />
        </div>
        {departments.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant={!deptFilter ? 'default' : 'outline'} className={!deptFilter ? 'bg-teal-500 text-white rounded-lg' : 'border-slate-200 text-slate-600 rounded-lg'} onClick={() => setDeptFilter('')}>All</Button>
            {departments.map(d => (
              <Button key={d} size="sm" variant={deptFilter === d ? 'default' : 'outline'} className={deptFilter === d ? 'bg-teal-500 text-white rounded-lg' : 'border-slate-200 text-slate-600 rounded-lg'} onClick={() => setDeptFilter(d)}>{d}</Button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 mt-4"><div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" /></div>
      ) : employees.length === 0 ? (
        <div className="glass-card p-12 mt-4 text-center"><p className="text-slate-500">No employees found.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4" data-testid="employee-grid">
          {employees.map((emp) => {
            const rc = roleColors[emp.role] || roleColors.teacher;
            return (
              <Link to={`/dashboard/employee/${emp.user_id}`} key={emp.user_id} className="glass-card p-5 group cursor-pointer hover:border-teal-300 hover:shadow-md transition-all" data-testid={`emp-card-${emp.user_id}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {photoUrls[emp.user_id] ? (
                      <img src={photoUrls[emp.user_id]} alt={emp.full_name} className="h-14 w-14 rounded-xl object-cover border border-slate-200" />
                    ) : (
                      <div className={`h-14 w-14 rounded-xl bg-gradient-to-br ${rc.gradient} flex items-center justify-center`}>
                        <span className="text-lg font-bold text-white">{emp.full_name?.charAt(0) || '?'}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-slate-800 truncate group-hover:text-teal-600 transition-colors">{emp.full_name}</h3>
                    {emp.title && <p className="text-xs text-slate-500 truncate">{emp.title}</p>}
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Mail className="h-3 w-3 text-slate-300" />
                      <p className="text-xs text-slate-400 truncate">{emp.email}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold ${rc.bg} ${rc.text}`}>
                        <Shield className="h-2.5 w-2.5 mr-1" />{rc.label}
                      </span>
                      {emp.department && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-500">
                          <Building className="h-2.5 w-2.5 mr-1" />{emp.department}
                        </span>
                      )}
                      {emp.years_of_service != null && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-500">
                          <Calendar className="h-2.5 w-2.5 mr-1" />{emp.years_of_service}y
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-200 group-hover:text-teal-500 transition-colors mt-1" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
